import { Contact } from '../types';
import { HelpdeskTicketInterface } from 'shared/types/helpdesk';

/**
 * Converts a helpdesk ticket's tenant (reporter) to a mass-mail Contact
 */
export const convertTicketTenantToContact = (ticket: HelpdeskTicketInterface): Contact | null => {
  if (!ticket.reporter) {
    console.warn('No reporter found in ticket', ticket.id);
    return null;
  }

  const reporter = ticket.reporter as any;
  const email = reporter.email || '';
  const firstName = reporter.firstName || '';
  const lastName = reporter.lastName || '';
  const name = `${firstName} ${lastName}`.trim() || 'Unknown Contact';

  // Build unit information
  const unitInfo = ticket.unit ? {
    id: ticket.unit.id || '',
    unitId: ticket.unit.id || '',
    unitNumber: (ticket.unit as any).boxNumber || ticket.unit.id || '',
    referencePlus: (ticket.unit as any).referencePlus || '',
    buildingId: ticket.building?.id || '',
    buildingName: (ticket.building as any)?.location?.formattedAddress || ticket.building?.id || '',
  } : null;

  const contact: Contact = {
    id: reporter.id || `tenant-${ticket.id}`,
    name,
    firstName,
    lastName,
    email,
    resourceId: reporter.resourceId || reporter.id || '',
    phoneMobile: reporter.phoneMobile || reporter.metadata1 || '',
    units: unitInfo ? [unitInfo] : [],
    // Set primary building/unit info
    buildingId: ticket.building?.id || '',
    buildingName: (ticket.building as any)?.location?.formattedAddress || ticket.building?.id || '',
    unitId: ticket.unit?.id || '',
    unitNumber: unitInfo?.unitNumber || '',
  };

  return contact;
};

/**
 * Converts a helpdesk ticket's supplier to a mass-mail Contact
 */
export const convertTicketSupplierToContact = (ticket: HelpdeskTicketInterface): Contact | null => {
  // Multi-leverancier: bron van waarheid is supplierAssignments (gesorteerd op
  // sortOrder). Val terug op het legacy `supplier`-veld indien nog aanwezig.
  const primaryAssignment = [...(ticket.supplierAssignments ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  )[0];
  const supplier = (primaryAssignment?.supplier ?? ticket.supplier) as any;
  if (!supplier) {
    console.warn('No supplier found in ticket', ticket.id);
    return null;
  }
  const email = supplier.email || '';
  const name = supplier.name || 'Unknown Supplier';

  // For suppliers, we use their contact info differently
  const contact: Contact = {
    id: supplier.id || `supplier-${ticket.id}`,
    name,
    firstName: name, // Suppliers might not have first/last name split
    lastName: '',
    email,
    resourceId: supplier.id || '',
    phoneMobile: supplier.phone || supplier.phoneMobile || '',
    units: [], // Suppliers don't have units
    buildingId: ticket.building?.id || '',
    buildingName: (ticket.building as any)?.location?.formattedAddress || ticket.building?.id || '',
    unitId: '',
    unitNumber: '',
  };

  return contact;
};

/**
 * Main utility function to convert ticket recipient based on communication mode
 */
export const convertTicketRecipientToContact = (
  ticket: HelpdeskTicketInterface, 
  communicationMode: 'tenant' | 'supplier'
): Contact | null => {
  if (communicationMode === 'supplier') {
    return convertTicketSupplierToContact(ticket);
  }
  return convertTicketTenantToContact(ticket);
};
